import {
  CompareConfigItem,
  isNotEqual,
  optionalStringsDiffer,
  regionIdsDiffer,
  relatedGeoEventsDiffer,
  useGeoEvent,
  UseGeoEventEditableResult,
} from 'features/geoEvents/hooks/useGeoEvent';
import { Flooding, FloodingCreateUpdateData } from '../../flooding.types';

export type UseFloodingGeoEventProps = {
  id?: number;
  enabled?: boolean;
};

export type UseFloodingGeoEvent = UseGeoEventEditableResult<
  Flooding,
  FloodingCreateUpdateData
>;

/**
 * Flooding-specific data field configurations for constructPatch
 */
export const floodingDataPatchConfigs: Record<
  keyof FloodingCreateUpdateData['data'],
  CompareConfigItem
> = {
  isParent: {
    shouldPatch: isNotEqual,
  },
  areaImpacted: {
    shouldPatch: optionalStringsDiffer,
  },
  links: {
    shouldPatch: isNotEqual,
  },
  internalLinks: {
    shouldPatch: isNotEqual,
  },
  reporterOnlyNotes: {
    shouldPatch: optionalStringsDiffer,
  },
  temporarilyDisplayEvacZones: { shouldPatch: isNotEqual },
  hasCustomEvacuationOrders: { shouldPatch: isNotEqual },
  hasCustomEvacuationWarnings: { shouldPatch: isNotEqual },
  hasCustomEvacuationAdvisories: { shouldPatch: isNotEqual },
  hasCustomEvacuationShelterInPlace: { shouldPatch: isNotEqual },
  evacuationOrders: { shouldPatch: optionalStringsDiffer },
  evacuationWarnings: { shouldPatch: optionalStringsDiffer },
  evacuationNotes: { shouldPatch: optionalStringsDiffer },
  evacuationAdvisories: { shouldPatch: optionalStringsDiffer },
  evacuationShelterInPlace: { shouldPatch: optionalStringsDiffer },
  urgency: { shouldPatch: isNotEqual },
  manualDeactivationStarted: { shouldPatch: isNotEqual },
  unstructuredEvacuationRegionIds: { shouldPatch: regionIdsDiffer },
  // These fields are never sent with PATCH
  geoEventType: { shouldPatch: () => false },
  reporterOnlyNotesDateModified: { shouldPatch: () => false },
};

export const floodingFieldPatchConfigs: Record<string, CompareConfigItem> = {
  parentGeoEvents: {
    shouldPatch: relatedGeoEventsDiffer,
  },
  reporterNotes: {
    shouldPatch: isNotEqual,
  },
};

export const useFloodingGeoEvent = (
  props: UseFloodingGeoEventProps,
): UseFloodingGeoEvent => {
  return useGeoEvent<Flooding, FloodingCreateUpdateData>({
    ...props,
    dataPatchConfigs: floodingDataPatchConfigs,
    fieldPatchConfigs: floodingFieldPatchConfigs,
  });
};
